import {useCallback, useEffect} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import styled from 'styled-components';

import Textbox from 'components/Textbox';
import {transparentize} from 'polished';

const Add = styled.div`
  text-decoration: underline;
  font-size: 18px;
  color: ${({theme}) => theme.link.default};
  cursor: pointer;

  &:hover,
  &:focus {
    color: ${({theme}) => theme.link.hover};
  }

  &:active {
    color: ${({theme}) => theme.link.active};
  }
`;

const Delete = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 0 16px;
  background: ${({theme, $disabled}) => transparentize($disabled ? 0.5 : 0, theme.button.secondary.default.bg)};
  width: 48px;
  height: 48px;
  border-radius: 60px;
  cursor: ${({$disabled}) => ($disabled ? 'default' : 'pointer')};
  /* pointer-events: ${({$disabled}) => ($disabled ? 'none' : 'all')}; */

  & svg {
    opacity: ${({$disabled}) => ($disabled ? 0.5 : 1)};
  }

  /* &:hover::before { */
  &:hover::before {
    display: ${({$disabled}) => ($disabled ? 'block' : 'none')};
    content: 'Required field';
    position: absolute;
    top: -2.25rem;
    width: 100px;
    text-align: center;
    border-radius: 4px;
    background: ${({theme}) => theme.tooltip.bg};
    color: ${({theme}) => theme.tooltip.text};
    font-size: 0.8rem;
  }

  &:hover::after {
    display: ${({$disabled}) => ($disabled ? 'block' : 'none')};
    content: '';
    position: absolute;
    top: -0.75rem;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 7px;
    border-color: transparent;
    border-top-color: ${({theme}) => theme.tooltip.bg};
  }

  &:hover,
  &:focus {
    background: ${({theme, $disabled}) => ($disabled
    ? transparentize(0.5, theme.button.secondary.default.bg)
    : theme.button.secondary.hover.bg)};
  }

  &:active {
    background: ${({theme, $disabled}) => ($disabled
    ? transparentize(0.5, theme.button.secondary.default.bg)
    : theme.button.secondary.active.bg)};
  }
`;

const Item = styled.li`
  display: flex;
  width: 100%;
  margin: 0 0 20px;

  & ${Textbox} {
    flex: 1;
  }
`;

const fieldDefaultValue = [];

const ItemList = ({
  name, addLabel = 'Add', placeholders = [], rules = {}, maxLength = null,
}) => {
  const {
    register, setError, clearErrors, errors, watch,
  } = useFormContext();
  const {
    fields, append, remove,
  } = useFieldArray({
    name,
  });
  const field = watch(name, fieldDefaultValue);
  const add = useCallback(() => {
    append({value: ''});
  }, [append]);
  const del = useCallback((idx) => () => {
    remove(idx);
  }, [remove]);
  useEffect(() => {
    if (rules && rules.minItems > 0) {
      if (
        (!errors[name] || !errors[name].type === 'minItems')
        && field.filter((val) => val.value).length < rules.minItems
      ) {
        setError(name, {
          type: 'minItems',
          message: `You must have at least ${rules.minItems} item${rules.minItems > 1 ? 's' : ''}`,
        });
      } else if (
        errors[name]
        && errors[name].type === 'minItems'
        && field.filter((val) => val.value).length >= rules.minItems
      ) {
        clearErrors(name);
      }
    }
  }, [field, errors, rules, name, setError, clearErrors]);

  return (
    <div>
      <ol>
        {fields.map((item, idx) => (
          <Item key={item.id}>
            <Textbox
              name={`${name}[${idx}].value`}
              defaultValue={item.value}
              ref={register(rules)}
              placeholder={placeholders.length ? placeholders[idx % placeholders.length] : ''}
              maxLength={maxLength}
            />
            <Delete
              title="Remove Item"
              onClick={!rules.minItems || idx >= rules.minItems ? del(idx) : null}
              $disabled={rules.minItems && idx < rules.minItems}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.5 6.5L12 1M6.5 6.5L1 12M6.5 6.5L12 12M6.5 6.5L1 1" stroke="#1A1715" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </Delete>
          </Item>
        ))}
      </ol>
      <Add onClick={add}>{addLabel}</Add>
    </div>
  );
};

export default ItemList;
